import React, { memo, useEffect, useState } from 'react'
import * as THREE from 'three'
import { FURNITURE_CONFIG, OpeningType } from '../furnitureConfig'
import { applyMaterialToObject } from '../materials/applyMaterial'
import { disposeObject } from '../three/disposeObject'
import { setupProfileHandle, setupRoundHandle } from '../handleUtils'
import {
  createDrawerGroup,
  calculateDrawerDimensions,
  setupDrawerFrontPanel,
  setupDrawerBottomPanel,
  setupDrawerLeftPanel,
  setupDrawerRightPanel,
  setupDrawerBackPanel,
} from '../drawerUtils'
import { useDrawerAnimation } from '~/hooks/useDrawerAnimation'

interface DrawerProps {
  horizontalPanelObject: THREE.Object3D
  roundHandleObject?: THREE.Object3D
  profileHandleObject: THREE.Object3D
  openingType: OpeningType
  drawerWidth: number
  drawerHeight: number
  drawerDepth: number
  selectedColor: string
  drawerIndex: number
  positionY: number
  positionX?: number
  positionZ?: number
  drawerOffsetZ?: number
  lerpSpeed?: number
  isHovered?: boolean
  rotateTexture?: boolean
}

const DrawerComponent: React.FC<DrawerProps> = ({
  horizontalPanelObject,
  roundHandleObject,
  profileHandleObject,
  drawerWidth,
  drawerHeight,
  drawerDepth,
  selectedColor,
  drawerIndex,
  positionY,
  positionX = 0,
  positionZ = 0,
  drawerOffsetZ = 15,
  lerpSpeed = 0.15,
  openingType,
  isHovered = false,
  rotateTexture,
}) => {
  // Build drawer in an effect (not useMemo) so create+dispose are paired
  // across StrictMode's mount→unmount→mount cycle. A useMemo would cache
  // the disposed clone on the second mount and render an empty scene.
  const [drawerGroup, setDrawerGroup] = useState<THREE.Group | null>(null)

  useEffect(() => {
    const built = createDrawerGroup(
      horizontalPanelObject,
      roundHandleObject || new THREE.Object3D(),
      profileHandleObject,
      drawerIndex
    )
    if (!built) return

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDrawerGroup(built)

    return () => {
      disposeObject(built)
       
      setDrawerGroup(null)
    }
  }, [horizontalPanelObject, roundHandleObject, profileHandleObject, drawerIndex])

  // Scale and position the drawer parts
  useEffect(() => {
    if (!drawerGroup) return

    const { handleOnTheDrawerTopOffset } = FURNITURE_CONFIG

    // Calculate drawer dimensions
    const { innerHeight, innerWidth, innerDepth } = calculateDrawerDimensions(
      drawerWidth,
      drawerHeight,
      drawerDepth
    )

    // Position drawer group
    const baseZ = positionZ
    drawerGroup.position.set(positionX, positionY, baseZ)
    drawerGroup.userData.baseZ = baseZ

    // Setup all drawer panels
    drawerGroup.children.forEach((panelPivot) => {
      if (panelPivot.userData.isDrawerFront) {
        setupDrawerFrontPanel(panelPivot, drawerWidth, innerHeight, drawerDepth)
      } else if (panelPivot.userData.roundHandle) {
        panelPivot.visible = openingType === OpeningType.RoundHandle

        setupRoundHandle(
          panelPivot,
          selectedColor,
          innerHeight,
          drawerDepth,
          handleOnTheDrawerTopOffset
        )
      } else if (panelPivot.userData.profileHandle) {
        panelPivot.visible = openingType === OpeningType.ProfileHandle

        setupProfileHandle(
          panelPivot,
          selectedColor,
          innerHeight,
          drawerDepth,
          {
            parentWidth: innerWidth,
          }
        )
      } else if (panelPivot.userData.isDrawerLeft) {
        setupDrawerLeftPanel(panelPivot, innerHeight, innerWidth, innerDepth)
      } else if (panelPivot.userData.isDrawerRight) {
        setupDrawerRightPanel(panelPivot, innerHeight, innerWidth, innerDepth)
      } else if (panelPivot.userData.isDrawerBack) {
        setupDrawerBackPanel(panelPivot, innerHeight, innerWidth)
      } else if (panelPivot.userData.isDrawerBottom) {
        setupDrawerBottomPanel(panelPivot, innerWidth, innerDepth)
      }
    })
  }, [
    drawerGroup,
    drawerWidth,
    drawerHeight,
    drawerDepth,
    positionY,
    positionX,
    positionZ,
    openingType,
    selectedColor,
  ])

  // Apply the selected color
  useEffect(() => {
    if (!drawerGroup) return

    // Apply material to drawer panels (handles are colored by setupRoundHandle/setupProfileHandle)
    drawerGroup.children.forEach((child) => {
      if (!child.userData.roundHandle && !child.userData.profileHandle) {
        // Apply material with PBR texture support to drawer panels.
        // If `rotateTexture` is provided by the caller it wins, otherwise
        // default to rotating only the drawer front panel.
        const shouldRotate =
          rotateTexture ?? child.userData.isDrawerFront === true
        applyMaterialToObject(child, selectedColor, shouldRotate)
      }
    })
  }, [drawerGroup, selectedColor, rotateTexture])

  // Opening animation
  useDrawerAnimation({
    drawerGroup,
    isHovered,
    drawerOffsetZ,
    lerpSpeed,
  })

  if (!drawerGroup) return null

  return <primitive object={drawerGroup} />
}

export const Drawer = memo(DrawerComponent)
